import { useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import SearchButton from "../../../../components/SearchButton";
import RefreshButton from "../../../../components/RefreshButton";
import { BarsArrowUpIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import SelectInput from "../../../../components/SelectInput";
import MultiSelectInput from "../../../../components/MultiSelectInput";
import { Disclosure } from "@headlessui/react";
import { useEmployeeTypeFilter } from "./hooks/useEmployeeType";
import { useEmployeeStatusFilter } from "./hooks/useEmployeeStatus";
import { useEmploymentTypeFilter } from "./hooks/useEmploymentType";
import { useEmployeeTitleFilter } from "./hooks/useEmployeeTitle";
import { useBusinessOperation } from "./hooks/useBusinessOperation";
import { empFilterValues } from "./atoms/atoms";
import { useGetOfficesFromContext } from "../../OfficeSettings/hooks/useSFGetOffice";
import { useGetAcquisitionName } from "./hooks/useGetAcquisitionNameList";
import { useGetBrand } from "modules/CarrierAgreementMgmt/OfficeSettings/hooks/useGetBrand";
import { permissionsSelector } from "modules/Login/selectors/permissionSelector";
import { PERMISSIONS } from "modules/PermissionsConstants";
import { accountAtom } from "../../../Login/atoms/AccountAtom";
import PropTypes from "prop-types";

const TableFilters = ({
  searchRefetch,
  setIsSearching,
  isSearching,
  EmployeesAsyncData,
  empList,
  searchClicked,
  setResetKey,
}) => {
  const user = useRecoilValue(accountAtom);
  //Permission
  const userPermissions = useRecoilValue(permissionsSelector);
  const usViewPermission =
    PERMISSIONS.permission_carrieragreemnentmgmt_employee_view;

  const isUsView = userPermissions?.includes(usViewPermission);
  const hasUSView = (user.isFranchise || user.isNonFranchise) && isUsView;

  const franchiseOnly =
    user.isFranchise && !user.isNonFranchise && !user.isMexico;

  //Filter based on role permission
  const filterByUsedInMexico = (data = []) => {
    return Array.isArray(data)
      ? data.filter((item) => {
          if (franchiseOnly) {
            return item.usedInMexico === false || item.usedInMexico === null;
          }
          return true; // All data
        })
      : [];
  };

  const { data: statusData, isLoading: statusLoading } =
    useEmployeeStatusFilter();

  const filteredEmpStatus = filterByUsedInMexico(statusData);
  const empStatus = filteredEmpStatus
    ?.map((empStatus) => ({
      label: empStatus.employeeStatusName,
      value: empStatus.employeeStatusId,
    }))
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: empTypeData, isLoading: empTypeLoading } =
    useEmployeeTypeFilter();
  const filteredEmpType = filterByUsedInMexico(empTypeData);
  const empType = filteredEmpType
    ?.filter((x) => {
      if (franchiseOnly) return x.employeeTypeName === "Franchise";
      if (!user.isFranchise) return x.employeeTypeName !== "Franchise";
      return true;
    })
    ?.map((empType) => {
      return {
        label: empType.employeeTypeName,
        value: empType.employeeTypeId,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: empntTypeData, isLoading: empntTypeLoading } =
    useEmploymentTypeFilter();
  const empntType = empntTypeData
    ?.map((empntType) => {
      return {
        label: empntType.employmentTypeName,
        value: empntType.employmentTypeId,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: titleData, isLoading: titleLoading } = useEmployeeTitleFilter();
  const filteredEmpTitle = filterByUsedInMexico(titleData?.data);
  const sfEmpTitleOpt = filteredEmpTitle
    ?.map((empTitle) => {
      return {
        value: empTitle.employeeTitleId,
        label: empTitle.employeeTitleName,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: busOpsData, isLoading: busOpsLoading } = useBusinessOperation();
  const empBusinessOperation = busOpsData
    ?.filter((x) => x.isActive)
    .map((empBusinessOperation) => {
      return {
        label: empBusinessOperation.businessOperationName,
        value: empBusinessOperation.businessOperationId,
      };
    });

  const { data: officeLocation, isLoading: officeLoading } =
    useGetOfficesFromContext();
  const officeLocationOpt = officeLocation
    ?.filter((x) => {
      const country = (x.country ?? "").toLowerCase();
      // All data
      if ((hasUSView && user.isMexico) || (!hasUSView && !user.isMexico)) {
        return true;
      }
      // US data
      if (franchiseOnly) {
        return country === "united states";
      }
      // Mexico data
      if (user.isMexico && !user.isFranchise && !user.isNonFranchise) {
        return country === "mexico";
      }
      return false;
    })
    .map((s) => {
      return {
        value: s.officeCode,
        label: s.officeCode + "-" + s.officeName,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const { data: brandData, isLoading: brandLoading } = useGetBrand();
  const filteredBrandData = filterByUsedInMexico(brandData);
  const officeBrandOpt = [
    ...new Map(
      filteredBrandData.map(item => [
        `${item.brandName} - ${item.state}`,
        item,
      ])
    ).values(),
  ].map(x => ({
    value: `${x.brandName} - ${x.state}`,
    label: `${x.brandName} - ${x.state}`,
  }));

  const { data: acquisitionNames, isFetching: acquisitionNameFetch } =
    useGetAcquisitionName();
  const filteredAcquisitionData = filterByUsedInMexico(acquisitionNames);
  const acquisitionNameOpt = filteredAcquisitionData
    ?.map((x) => {
      return {
        label: x.acquisitionName,
        value: x.acquisitionId,
      };
    })
    ?.sort((a, b) => a.label.trim().localeCompare(b.label.trim()));

  const countryOpt = [
    { label: "Mexico", value: "Mexico" },
    { label: "United States", value: "United States" },
  ].filter((country) => {
    if (franchiseOnly) {
      return country.value === "United States";
    }
    return true; // Show both
  });

  const isSplcOpts = [
    { label: "True", value: true },
    { label: "False", value: false },
  ];

  const DEFAULT_FILTER_VALUES = {
    StatusId: null,
    Status: "",
    employeeTypeId: null,
    employmentTypeId: null,
    businessOperation: "",
    title: null,
    acquisition: null,
    country: "",
    isSplc: null,
    location: "",
    selectedLocation: [],
    brandName: "",
    selectedBrand: [],
  };

  const refreshEmployees = () => {
    setFiltervalue(DEFAULT_FILTER_VALUES);
    setStatus("");
    setEmployeeType("");
    setEmploymentType("");
    setBusinessOperation("");
    setTitle("");
    setAcquisition("");
    setCountry("");
    setIsSplc(null);
    setIsSearching(false);
    setResetKey((prev) => prev + 1);
    searchClicked();
  };

  const [status, setStatus] = useState("");
  const [employeeType, setEmployeeType] = useState("");
  const [employmentType, setEmploymentType] = useState("");
  const [title, setTitle] = useState("");
  const [businessOperation, setBusinessOperation] = useState("");
  const [filteredValue, setFiltervalue] = useRecoilState(empFilterValues);
  const [acquisition, setAcquisition] = useState("");
  const [country, setCountry] = useState("");
  const [isSplc, setIsSplc] = useState(null);

  const setterMap = {
    Status: setStatus,
    EmployeeType: setEmployeeType,
    BusinessOperation: setBusinessOperation,
    Title: setTitle,
    EmploymentType: setEmploymentType,
    Acquisition: setAcquisition,
    Country: setCountry,
    IsSplc: setIsSplc,
  };

  const handleSelection = (fieldName, checkedValue) => {
    const setter = setterMap[fieldName];
    if (setter) setter(checkedValue);
  };

  return (
    <div className="pr-3 space-y-2 mt-4">
      <div>
        <BarsArrowUpIcon className="h-6 w-6 text-primary mb-1.5" />
        <div className="text-xl font-bold text-primary -mt-9 ml-8">
          Search Section
        </div>
      </div>
      <div className="w-full ">
        <div className="w-full bg-white rounded-2xl">
          <Disclosure defaultOpen={isSearching}>
            {({ open }) => (
              <>
                <Disclosure.Button className="sm:px-2 flex justify-between w-full px-4 py-2 text-sm font-medium text-left text-primary bg-gray-100 rounded-t-lg hover:bg-gray-200 focus:outline-none focus-visible:ring focus-visible:ring-purple-500 focus-visible:ring-opacity-75">
                  <span>By Employee Details</span>
                  <ChevronDownIcon
                    className={`${
                      open ? "transform rotate-180" : ""
                    } w-5 h-5 text-gray-500`}
                  />
                </Disclosure.Button>
                <Disclosure.Panel className="bg-gray-100 text-sm text-primary rounded-b-lg">
                  <div className="bg-gray-100  p-3 sm:px-2 md:px-3">
                    <div className="flex flex-col text-xs gap-x-4 gap-y-2">
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Status
                        </label>
                        <div className="w-full">
                          <SelectInput
                            isLoading={statusLoading}
                            options={empStatus}
                            onChange={(e) => {
                              handleSelection("Status", {
                                id: e?.value,
                                label: e?.label,
                              });
                              setFiltervalue((old) => ({
                                ...old,
                                StatusId: e?.value,
                                Status: e?.label,
                              }));
                            }}
                            value={empStatus?.find(
                              (x) =>
                                x.value === (filteredValue?.StatusId || status)
                            )}
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Employee Type
                        </label>
                        <div className="w-full">
                          <SelectInput
                            isLoading={empTypeLoading}
                            options={empType}
                            onChange={(e) => {
                              handleSelection("EmployeeType", e?.value);
                              setFiltervalue((old) => {
                                return {
                                  ...old,
                                  employeeTypeId: e?.value,
                                };
                              });
                            }}
                            value={
                              employeeType
                                ? empType?.filter(
                                    (x) => x.value === employeeType
                                  )
                                : empType?.filter(
                                    (x) =>
                                      x.value === filteredValue.employeeTypeId
                                  )
                            }
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Business Operation
                        </label>
                        <div className="w-full">
                          <SelectInput
                            isLoading={busOpsLoading}
                            options={empBusinessOperation}
                            onChange={(e) => {
                              handleSelection("BusinessOperation", e?.value);
                              setFiltervalue((old) => {
                                return { ...old, businessOperation: e?.label };
                              });
                            }}
                            value={
                              businessOperation
                                ? empBusinessOperation?.filter(
                                    (x) => x.value === businessOperation
                                  )
                                : empBusinessOperation?.filter(
                                    (x) =>
                                      x.label ===
                                      filteredValue.businessOperation
                                  )
                            }
                          />
                        </div>
                      </div>

                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Title
                        </label>
                        <div className="w-full">
                          <SelectInput
                            options={sfEmpTitleOpt}
                            onChange={(e) => {
                              handleSelection("Title", e?.value);
                              setFiltervalue((old) => {
                                return {
                                  ...old,
                                  title: e?.value,
                                };
                              });
                            }}
                            isLoading={titleLoading}
                            value={
                              title
                                ? sfEmpTitleOpt?.filter(
                                    (x) => x.value === title
                                  )
                                : sfEmpTitleOpt?.filter(
                                    (x) => x.value === filteredValue.title
                                  )
                            }
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Employment Type
                        </label>
                        <div className="w-full">
                          <SelectInput
                            isLoading={empntTypeLoading}
                            options={empntType}
                            onChange={(e) => {
                              handleSelection("EmploymentType", e?.value);
                              setFiltervalue((old) => {
                                return {
                                  ...old,
                                  employmentTypeId: e?.value,
                                  employmentType: e?.label,
                                };
                              });
                            }}
                            value={
                              employmentType
                                ? empntType?.filter(
                                    (x) => x.value === employmentType
                                  )
                                : empntType?.filter(
                                    (x) =>
                                      x.value === filteredValue.employmentTypeId
                                  )
                            }
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Office Location
                        </label>
                        <div className="w-full">
                          <MultiSelectInput
                            isLoading={officeLoading}
                            options={officeLocationOpt}
                            value={
                              filteredValue.selectedLocation?.length > 0
                                ? filteredValue.selectedLocation
                                : ""
                            }
                            handleMultiSelect={(e) => {
                              setFiltervalue({
                                ...filteredValue,
                                location:
                                  e?.length > 0 ? e.map((x) => x.value) : "",
                                selectedLocation: e?.length > 0 ? e : [],
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Acquisition Name
                        </label>
                        <div className="w-full">
                          <SelectInput
                            options={acquisitionNameOpt}
                            onChange={(e) => {
                              handleSelection("Acquisition", e?.value);
                              setFiltervalue((old) => {
                                return { ...old, acquisition: e?.value };
                              });
                            }}
                            value={
                              acquisition
                                ? acquisitionNameOpt?.filter(
                                    (x) => x.value === acquisition
                                  )
                                : acquisitionNameOpt?.filter(
                                    (x) => x.value === filteredValue.acquisition
                                  )
                            }
                            isLoading={acquisitionNameFetch}
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Office Brand
                        </label>
                        <div className="w-full">
                          <MultiSelectInput
                            isLoading={brandLoading}
                            options={
                              officeBrandOpt?.length > 0 ? officeBrandOpt : []
                            }
                            value={
                              filteredValue.selectedBrand?.length > 0
                                ? filteredValue.selectedBrand
                                : ""
                            }
                            handleMultiSelect={(e) => {
                              setFiltervalue({
                                ...filteredValue,
                                brandName:
                                  e?.length > 0 ? e.map((x) => x.value) : "",
                                selectedBrand: e?.length > 0 ? e : [],
                              });
                            }}
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Country
                        </label>
                        <div className="w-full">
                          <SelectInput
                            options={countryOpt}
                            onChange={(e) => {
                              handleSelection("Country", e?.value);
                              setFiltervalue((old) => {
                                return { ...old, country: e?.value };
                              });
                            }}
                            value={
                              country
                                ? countryOpt.filter((x) => x.value === country)
                                : countryOpt.filter(
                                    (x) => x.value === filteredValue.country
                                  )
                            }
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <label className="block font-medium text-gray-700 mb-2">
                          Is Splc
                        </label>
                        <div className="w-full">
                          <SelectInput
                            placeholder="Select..."
                            options={isSplcOpts}
                            onChange={(e) => {
                              const value =
                                typeof e?.value === "boolean" ? e.value : null;
                              handleSelection("IsSplc", value);
                              setFiltervalue((old) => ({
                                ...old,
                                isSplc: value,
                              }));
                            }}
                            value={
                              isSplcOpts.find(
                                (x) =>
                                  x.value ===
                                  (typeof isSplc === "boolean"
                                    ? isSplc
                                    : filteredValue.isSplc)
                              ) || null
                            }
                          />
                        </div>
                      </div>

                      <div className="place-self-center">
                        <div className="flex flex-row gap-3 justify-center m-3">
                          <SearchButton
                            buttonValue="Search"
                            onClick={() => {
                              searchRefetch();
                              setIsSearching(true);
                              EmployeesAsyncData(empList);
                              searchClicked();
                            }}
                          />
                          <RefreshButton
                            onClick={() => {
                              refreshEmployees();
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
      </div>
    </div>
  );
};

TableFilters.propTypes = {
  searchRefetch: PropTypes.func,
  setIsSearching: PropTypes.func,
  isSearching: PropTypes.bool,
  EmployeesAsyncData: PropTypes.func,
  empList: PropTypes.array,
  searchClicked: PropTypes.func,
  setResetKey: PropTypes.func,
};

export default TableFilters;
